import { useMemo } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useScanEmployeeReportsQuery } from '@/store/api/apiSlice';
import { CATEGORY_LABELS, SEVERITY_PILL, matchLabel, matchStrength } from '@/utils/employeeReports';
import type { EmployeeReportScanMatch, EmployeeReportScanParams } from '@/types';

interface Props {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    nin?: string | null;
}

// Staff-facing banner on the investigation detail page: scans the derogatory
// report registry for the subject's identity and warns if they've been reported
// before. `matchedOn` drives whether each hit is a strong (NIN/email/phone) or
// weak (name-only) match.
export default function PreviouslyReportedBanner({ name, email, phone, nin }: Props) {
    const params: EmployeeReportScanParams = {};
    if (name?.trim()) params.name = name.trim();
    if (email?.trim()) params.email = email.trim();
    if (phone?.trim()) params.phone = phone.trim();
    if (nin?.trim()) params.nin = nin.trim();
    const hasIdentifier = Object.keys(params).length > 0;

    const { data, isLoading } = useScanEmployeeReportsQuery(params, { skip: !hasIdentifier });

    const matches = useMemo<EmployeeReportScanMatch[]>(() => data ?? [], [data]);
    const strongCount = useMemo(() => matches.filter((m) => matchStrength(m.matchedOn) === 'strong').length, [matches]);

    if (!hasIdentifier || isLoading || matches.length === 0) return null;

    const allWeak = strongCount === 0;

    return (
        <div
            className={`mb-6 rounded-xl border px-4 py-3 ${
                allWeak ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'
            }`}
        >
            <div className="flex items-start gap-3">
                <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${allWeak ? 'text-amber-600' : 'text-red-600'}`} />
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${allWeak ? 'text-amber-800' : 'text-red-800'}`}>
                        Previously reported ({matches.length})
                    </p>
                    <p className={`mt-0.5 text-xs ${allWeak ? 'text-amber-700' : 'text-red-700'}`}>
                        {strongCount > 0
                            ? `${strongCount} strong match${strongCount === 1 ? '' : 'es'} on a unique identifier (NIN, email or phone).`
                            : 'Name-only match — verify identity before relying on this, people share names.'}
                    </p>

                    <ul className="mt-3 flex flex-col gap-2">
                        {matches.map((m) => {
                            const strong = matchStrength(m.matchedOn) === 'strong';
                            return (
                                <li
                                    key={m.id}
                                    className="flex flex-wrap items-center gap-2 rounded-lg border border-white/60 bg-white/70 px-3 py-2"
                                >
                                    <span className="text-xs font-semibold text-[#1b1b1b]">
                                        {CATEGORY_LABELS[m.category] ?? m.category}
                                    </span>
                                    <span
                                        className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_PILL[m.severity]}`}
                                    >
                                        {m.severity}
                                    </span>
                                    <span
                                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${
                                            strong
                                                ? 'border-red-200 bg-red-50 text-red-700'
                                                : 'border-amber-200 bg-amber-50 text-amber-700'
                                        }`}
                                    >
                                        {strong ? (
                                            <ShieldCheck className="h-3 w-3" />
                                        ) : (
                                            <AlertTriangle className="h-3 w-3" />
                                        )}
                                        {strong ? 'Strong' : 'Weak'} · matched {m.matchedOn.map(matchLabel).join(', ')}
                                    </span>
                                    {m.description && (
                                        <span className="w-full text-xs text-[#4e4d4d]">{m.description}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
